/**
 * Landing for /planning/plans — opens the plan of record in force for the whole
 * organization (migration 919), else the workspace primary plan cycle. The
 * Action area's "Plan of Record" link lands here. Catalog at ?catalog=1.
 */

import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import type { PlanCycle } from '../../types/planning';
import { resolvePrimaryPlanCycle } from '../../types/planning';
import { listPlanCycles } from '../../lib/planningService';
import { fetchPlanForceStates } from '../../lib/planForceService';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import PlansPage from './PlansPage';

export default function PlanningPlansSmartEntry() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [searchParams] = useSearchParams();
  const [cycles, setCycles] = useState<PlanCycle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inForceId, setInForceId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!workspaceId) {
        setCycles([]);
        return;
      }
      const [{ data, error: err }, states] = await Promise.all([listPlanCycles({ workspaceId }), fetchPlanForceStates(workspaceId)]);
      if (!alive) return;
      setInForceId(states?.find((r) => r.state === 'in_force' && r.scopeOrgUnitId === null)?.planCycleId ?? null);
      if (err) setError(err);
      setCycles(data ?? []);
    })();
    return () => {
      alive = false;
    };
  }, [workspaceId]);

  if (searchParams.get('catalog') === '1') {
    return <PlansPage />;
  }

  if (cycles === null) {
    return (
      <div className="px-6 py-10 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Loading Plan of Record…
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-6 mt-6 border border-luminous-coral-200 dark:border-luminous-coral-800 bg-luminous-coral-50 dark:bg-luminous-coral-900/40 px-4 py-2.5 text-sm text-luminous-coral-800 dark:text-luminous-coral-100">
        {error}
      </div>
    );
  }

  if (inForceId) {
    return <Navigate to={`/planning/plans/${inForceId}`} replace />;
  }
  const primary = resolvePrimaryPlanCycle(cycles);
  if (primary) {
    return <Navigate to={`/planning/plans/${primary.id}`} replace />;
  }

  return <PlansPage />;
}
